import {
  TrainingJobFromResponse,
  TrainingMethod,
  TrainingProgress,
  TrainingStatus
} from "@/types/training";
import { ControlledTuningIcon } from "./Icons/ControlledTuningIcon";
import { DPOIcon } from "./Icons/DPOIcon";
import { PPOIcon } from "./Icons/PPOIcon";
// import { PreTrainingIcon } from "./Icons/PreTrainingIcon";
import { SVGProps } from "react";
import { ChooseModelIcon } from "./Icons/ChooseModelIcon";
import { LinkHuggingFaceIcon } from "./Icons/LinkHuggingFaceIcon";
import { RewardModelingIcon } from "./Icons/RewardModelingIcon";
import { SupervisedLearningIcon } from "./Icons/SupervisedLearningIcon";

export const TRAINING_JOB_TABS = [
  {
    value: "chart",
    label: "Loss Chart",
    className: "px-0 py-3 m-0 subtext-small leading-5"
  },
  {
    value: "details",
    label: "Training Details",
    className: "px-0 py-3 ml-6 mr-0 subtext-small leading-5"
  },
  {
    value: "logs",
    label: "Training Logs",
    className: "px-0 py-3 ml-6 mr-0 subtext-small leading-5"
  }
];

export const TRAINING_METHOD_VALUE_LABEL_MAP: Record<TrainingMethod, string> = {
  SFT: "Supervised",
  REWARD: "Reward",
  PPO: "PPO",
  DPO: "DPO",
  KTO: "KTO"
  // pre_training: "Pre-Train"
};

export const TRAINING_METHOD_OPTIONS: {
  label: string;
  description: string;
  value: TrainingMethod;
  customIcon: React.FC<SVGProps<SVGSVGElement>>;
}[] = [
  {
    label: "Supervised Fine-Tuning",
    description: "Learn task-specific behavior from labeled data",
    value: "SFT",
    customIcon: SupervisedLearningIcon
  },
  {
    label: "Reward Modeling",
    description: "Train to score responses",
    value: "REWARD",
    customIcon: RewardModelingIcon
  },
  {
    label: "Proximal Policy Optimization",
    description: "Learn via reward feedback",
    value: "PPO",
    customIcon: PPOIcon
  },
  {
    label: "Direct Preference Optimization",
    description: "Directly learn from preference data",
    value: "DPO",
    customIcon: DPOIcon
  },
  {
    label: "Controlled Tuning Optimization",
    description: "KL - Regularized tuning (experimental)",
    value: "KTO",
    customIcon: ControlledTuningIcon
  }
  // {
  //   label: "Pre Training",
  //   description: "Learn from large text data",
  //   value: "pre_training",
  //   customIcon: PreTrainingIcon
  // }
];

export const SELECT_MODEL_OPTIONS = [
  {
    label: "Choose Model",
    description: "Choose from list of models",
    value: "modelListOption",
    customIcon: ChooseModelIcon
  },
  {
    label: "Link HuggingFace",
    description: "Link Model to our service",
    value: "hfModelOption",
    customIcon: LinkHuggingFaceIcon
  }
];

// Regex to validate Hugging Face URL
export const HUGGING_FACE_URL_REGEX = /^(https?:\/\/)?huggingface\.co(\/.*)?$/;

export const DATASET_ACCESS_ERROR_CODES = {
  gated: "This dataset is gated and requires authentication.",
  notFound: "Dataset not found or does not exist.",
  unauthorized: "Unauthorized access. Please check your credentials.",
  empty: "Dataset has no files or is empty.",
  failedAccess: "Failed to access dataset files",
  general: "An error occurred while accessing the dataset."
};

export const MODEL_ACCESS_ERROR_CODES = {
  gated: "This model is gated and requires authentication.",
  notFound: "Model not found or does not exist.",
  unauthorized: "Unauthorized access. Please check your credentials.",
  empty: "Model has no files or is empty.",
  failedAccess: "Failed to access model files",
  general: "An error occurred while accessing the model."
};

export const STATUS_COLOR_MAP: Record<TrainingStatus, string> = {
  created: "#737373",
  deploying: "#E6770A",
  deploy_failed: "#EF4444",
  training: "#40BA6F",
  completed: "#40BA6F",
  failed: "#EF4444",
  training_failed: "#EF4444",
  queued: "#737373"
};

export const JOBS_MOCK_DATA: TrainingJobFromResponse[] = [
  {
    id: "c7e75ec6-09d6-4403-b0f5-2d5e56d4dea1",
    user_id: "b4263d02-c426-4c05-91fc-a07e9ffc2e30",
    base_model_hf: "meta-llama/Llama-3.1-8B-Instruct",
    dataset_hf: "wiki_qa",
    ft_type: "SFT",
    caas_deployment_id: "d451350d-4c92-4f07-b21d-709326982128",
    container_endpoint:
      "https://e072e3c79922-d451350d-4c92-4f07-b21d-709326982128-caas.dev-user-hosted-content.io.solutions",
    llamafactory_job_id: "4f79327c-1c1f-4bfb-8b47-e76b7fae2e8f",
    additional_training_config: {},
    exported_hf_repo: null,
    status: {
      caas_status: "running",
      training_status: "running",
      progress: 20,
      remaining_time_hours: 23.966666666666665,
      unified_status: "training"
    },
    last_status: {
      progress: 0,
      fetched_at: "2025-07-03T17:53:03.731998",
      caas_status: "running",
      unified_status: "deploying",
      training_status: null,
      remaining_time_hours: 23.966666666666665
    },
    created_at: "2025-07-03T17:49:28.253112Z",
    end_time: null
  },
  {
    id: "eac464c6-d028-437e-b366-c6ec00a5f64e",
    user_id: "b4263d02-c426-4c05-91fc-a07e9ffc2e30",
    base_model_hf: "meta-llama/Llama-3.1-8B-Instruct",
    dataset_hf: "microsoft/wiki_qa",
    ft_type: "SFT",
    caas_deployment_id: "9bff1914-1599-4aed-b838-4524feb2a538",
    container_endpoint:
      "https://a60998d60ce3-9bff1914-1599-4aed-b838-4524feb2a538-caas.dev-user-hosted-content.io.solutions",
    llamafactory_job_id: "05ef3d22-838f-4a4a-a061-afa096eef312",
    additional_training_config: {},
    exported_hf_repo: null,
    status: {
      caas_status: "destroyed",
      training_status: null,
      progress: 0,
      remaining_time_hours: null,
      unified_status: "deploy_failed"
    },
    last_status: {
      progress: 60,
      fetched_at: "2025-07-03T17:53:05.589824",
      caas_status: "destroyed",
      unified_status: "deploy_failed",
      training_status: null,
      remaining_time_hours: null
    },
    created_at: "2025-07-03T17:05:32.141114Z",
    end_time: null
  },
  {
    id: "83b84bb3-c0dc-48d8-a542-6873859e1f54",
    user_id: "b4263d02-c426-4c05-91fc-a07e9ffc2e30",
    base_model_hf: "Llama-3-8B-Instruct",
    dataset_hf: "wikiqa",
    ft_type: "SFT",
    caas_deployment_id: "f451ada9-5b61-4ba5-b9a2-ce73e2343744",
    container_endpoint:
      "https://7ef12a1f51dd-f451ada9-5b61-4ba5-b9a2-ce73e2343744-caas.dev-user-hosted-content.io.solutions",
    llamafactory_job_id: "58d73211-1a26-41a4-8be4-cec11099cf14",
    additional_training_config: {},
    exported_hf_repo: null,
    status: {
      caas_status: "destroyed",
      training_status: null,
      progress: 70,
      remaining_time_hours: null,
      unified_status: "deploy_failed"
    },
    last_status: {
      progress: 0,
      fetched_at: "2025-07-03T17:53:07.469069",
      caas_status: "destroyed",
      unified_status: "deploy_failed",
      training_status: null,
      remaining_time_hours: null
    },
    created_at: "2025-07-03T16:53:32.865039Z",
    end_time: null
  },
  {
    id: "4358bd53-a595-4ac5-aefb-31a957107bf7",
    user_id: "b4263d02-c426-4c05-91fc-a07e9ffc2e30",
    base_model_hf: "Llama-3-8B-Instruct",
    dataset_hf: "wikiqa",
    ft_type: "SFT",
    caas_deployment_id: "7c43a301-50cf-4e38-96cc-fb6b5cd876b0",
    container_endpoint:
      "https://0193e6f9fd98-7c43a301-50cf-4e38-96cc-fb6b5cd876b0-caas.dev-user-hosted-content.io.solutions",
    llamafactory_job_id: null,
    additional_training_config: {},
    exported_hf_repo: null,
    status: {
      caas_status: "destroyed",
      training_status: null,
      progress: 0,
      remaining_time_hours: null,
      unified_status: "deploy_failed"
    },
    last_status: {
      progress: 0,
      fetched_at: "2025-07-03T17:53:08.357196",
      caas_status: "destroyed",
      unified_status: "deploy_failed",
      training_status: null,
      remaining_time_hours: null
    },
    created_at: "2025-07-03T14:59:49.944612Z",
    end_time: null
  },
  {
    id: "53efbf4a-87b6-4fb2-92ee-1be2b4396483",
    user_id: "b4263d02-c426-4c05-91fc-a07e9ffc2e30",
    base_model_hf: "Llama-3-8B-Instruct",
    dataset_hf: "wikiqa",
    ft_type: "SFT",
    caas_deployment_id: "843cc2c9-688f-4f64-adfb-26483c08cbec",
    container_endpoint:
      "https://a1cc87c8046c-843cc2c9-688f-4f64-adfb-26483c08cbec-caas.dev-user-hosted-content.io.solutions",
    llamafactory_job_id: null,
    additional_training_config: {
      max_samples: 10000
    },
    exported_hf_repo: "ilkhom199/my-finetuned-model",
    status: {
      caas_status: "destroyed",
      training_status: null,
      progress: 100,
      remaining_time_hours: null,
      unified_status: "deploy_failed"
    },
    last_status: {
      progress: 0,
      fetched_at: "2025-07-03T17:53:09.199933",
      caas_status: "destroyed",
      unified_status: "deploy_failed",
      training_status: null,
      remaining_time_hours: null
    },
    created_at: "2025-07-03T14:49:32.745182Z",
    end_time: null
  },
  {
    id: "a4765f52-6827-4166-93fb-bcbf24b78c1a",
    user_id: "b4263d02-c426-4c05-91fc-a07e9ffc2e30",
    base_model_hf: "Llama-3-8B-Instruct",
    dataset_hf: "wikiqa",
    ft_type: "SFT",
    caas_deployment_id: "7e49aeca-1f5f-4ea1-91e5-2d14e298a726",
    container_endpoint:
      "https://cbf25e07b3e0-7e49aeca-1f5f-4ea1-91e5-2d14e298a726-caas.dev-user-hosted-content.io.solutions",
    llamafactory_job_id: null,
    additional_training_config: {
      max_samples: 10000
    },
    exported_hf_repo: "ilkhom199/my-finetuned-model",
    status: {
      caas_status: "destroyed",
      training_status: null,
      progress: 100,
      remaining_time_hours: null,
      unified_status: "deploy_failed"
    },
    last_status: {
      progress: 0,
      fetched_at: "2025-07-03T17:53:10.113445",
      caas_status: "destroyed",
      unified_status: "deploy_failed",
      training_status: null,
      remaining_time_hours: null
    },
    created_at: "2025-07-03T14:41:23.664277Z",
    end_time: null
  },
  {
    id: "bbb8c6b8-bee0-4ac5-a567-61a9f8c322a2",
    user_id: "b4263d02-c426-4c05-91fc-a07e9ffc2e30",
    base_model_hf: "Llama-3-8B-Instruct",
    dataset_hf: "wikiqa",
    ft_type: "SFT",
    caas_deployment_id: "7b6e5692-d823-464e-8958-49f27edd5369",
    container_endpoint:
      "https://b453b33a5cc9-7b6e5692-d823-464e-8958-49f27edd5369-caas.dev-user-hosted-content.io.solutions",
    llamafactory_job_id: null,
    additional_training_config: {
      max_samples: 10000
    },
    exported_hf_repo: "ilkhom199/my-finetuned-model",
    status: {
      caas_status: "destroyed",
      training_status: null,
      progress: 0,
      remaining_time_hours: null,
      unified_status: "deploy_failed"
    },
    last_status: {
      progress: 0,
      fetched_at: "2025-07-03T17:53:10.936249",
      caas_status: "destroyed",
      unified_status: "deploy_failed",
      training_status: null,
      remaining_time_hours: null
    },
    created_at: "2025-07-03T14:30:37.593152Z",
    end_time: null
  }
];

export const MOCK_TRAINING_LOGS: TrainingProgress[] = [
  {
    current_steps: 5,

    total_steps: 237,

    loss: 2.9876,

    lr: 4.9964865700539986e-5,

    epoch: 0.064,

    percentage: 2.11,

    elapsed_time: "0:01:07",

    remaining_time: "0:52:18",

    throughput: 566.59,

    total_tokens: 38320
  },

  {
    current_steps: 10,

    total_steps: 237,

    loss: 2.6157,

    lr: 4.982230184254933e-5,

    epoch: 0.128,

    percentage: 4.22,

    elapsed_time: "0:01:41",

    remaining_time: "0:38:26",

    throughput: 761.0,

    total_tokens: 77328
  },

  {
    current_steps: 15,

    total_steps: 237,

    loss: 2.1706,

    lr: 4.957073807758763e-5,

    epoch: 0.192,

    percentage: 6.33,

    elapsed_time: "0:02:02",

    remaining_time: "0:30:14",

    throughput: 935.36,

    total_tokens: 114704
  },

  {
    current_steps: 20,

    total_steps: 237,

    loss: 1.9881,

    lr: 4.921127907463972e-5,

    epoch: 0.256,

    percentage: 8.44,

    elapsed_time: "0:02:28",

    remaining_time: "0:26:55",

    throughput: 1023.4,

    total_tokens: 152336
  },

  {
    current_steps: 25,

    total_steps: 237,

    loss: 1.8532,

    lr: 4.874550329319457e-5,

    epoch: 0.32,

    percentage: 10.55,

    elapsed_time: "0:02:46",

    remaining_time: "0:23:29",

    throughput: 1143.59,

    total_tokens: 190016
  },

  {
    current_steps: 30,

    total_steps: 237,

    loss: 1.7988,

    lr: 4.8175456051900256e-5,

    epoch: 0.384,

    percentage: 12.66,

    elapsed_time: "0:03:03",

    remaining_time: "0:21:04",

    throughput: 1247.31,

    total_tokens: 228496
  },

  {
    current_steps: 35,

    total_steps: 237,

    loss: 1.8107,

    lr: 4.750364054714302e-5,

    epoch: 0.448,

    percentage: 14.77,

    elapsed_time: "0:03:18",

    remaining_time: "0:19:02",

    throughput: 1349.02,

    total_tokens: 267120
  },

  {
    current_steps: 40,

    total_steps: 237,

    loss: 1.8169,

    lr: 4.673300686098957e-5,

    epoch: 0.512,

    percentage: 16.88,

    elapsed_time: "0:03:35",

    remaining_time: "0:17:40",

    throughput: 1415.07,

    total_tokens: 304640
  },

  {
    current_steps: 45,

    total_steps: 237,

    loss: 1.7665,

    lr: 4.586693900676116e-5,

    epoch: 0.576,

    percentage: 18.99,

    elapsed_time: "0:03:49",

    remaining_time: "0:16:18",

    throughput: 1496.01,

    total_tokens: 343200
  },

  {
    current_steps: 50,

    total_steps: 237,

    loss: 1.7373,

    lr: 4.4909240069124966e-5,

    epoch: 0.64,

    percentage: 21.1,

    elapsed_time: "0:04:05",

    remaining_time: "0:15:20",

    throughput: 1551.85,

    total_tokens: 381744
  },

  {
    current_steps: 55,

    total_steps: 237,

    loss: 1.6989,

    lr: 4.386411550395576e-5,

    epoch: 0.704,

    percentage: 23.21,

    elapsed_time: "0:04:24",

    remaining_time: "0:14:33",

    throughput: 1591.04,

    total_tokens: 420112
  },

  {
    current_steps: 60,

    total_steps: 237,

    loss: 1.6968,

    lr: 4.2736154671301556e-5,

    epoch: 0.768,

    percentage: 25.32,

    elapsed_time: "0:04:36",

    remaining_time: "0:13:36",

    throughput: 1654.29,

    total_tokens: 457744
  },

  {
    current_steps: 65,

    total_steps: 237,

    loss: 1.6583,

    lr: 4.15303106825461e-5,

    epoch: 0.832,

    percentage: 27.43,

    elapsed_time: "0:04:52",

    remaining_time: "0:12:54",

    throughput: 1696.32,

    total_tokens: 496288
  },

  {
    current_steps: 70,

    total_steps: 237,

    loss: 1.6726,

    lr: 4.025187865026311e-5,

    epoch: 0.896,

    percentage: 29.54,

    elapsed_time: "0:05:06",

    remaining_time: "0:12:12",

    throughput: 1740.71,

    total_tokens: 534192
  },

  {
    current_steps: 75,

    total_steps: 237,

    loss: 1.7029,

    lr: 3.890647243627218e-5,

    epoch: 0.96,

    percentage: 31.65,

    elapsed_time: "0:05:21",

    remaining_time: "0:11:33",

    throughput: 1779.23,

    total_tokens: 571472
  },

  {
    current_steps: 80,

    total_steps: 237,

    loss: 1.5518,

    lr: 3.7500000000000003e-5,

    epoch: 1.0128,

    percentage: 33.76,

    elapsed_time: "0:05:34",

    remaining_time: "0:10:57",

    throughput: 1798.79,

    total_tokens: 602368
  },

  {
    current_steps: 85,

    total_steps: 237,

    loss: 1.6173,

    lr: 3.60386374553978e-5,

    epoch: 1.0768,

    percentage: 35.86,

    elapsed_time: "0:05:47",

    remaining_time: "0:10:21",

    throughput: 1845.38,

    total_tokens: 641360
  },

  {
    current_steps: 90,

    total_steps: 237,

    loss: 1.5871,

    lr: 3.4528801950336174e-5,

    epoch: 1.1408,

    percentage: 37.97,

    elapsed_time: "0:06:03",

    remaining_time: "0:09:53",

    throughput: 1871.68,

    total_tokens: 680144
  },

  {
    current_steps: 95,

    total_steps: 237,

    loss: 1.6115,

    lr: 3.297712348756982e-5,

    epoch: 1.2048,

    percentage: 40.08,

    elapsed_time: "0:06:16",

    remaining_time: "0:09:22",

    throughput: 1909.97,

    total_tokens: 718240
  },

  {
    current_steps: 100,

    total_steps: 237,

    loss: 1.6508,

    lr: 3.139041581101187e-5,

    epoch: 1.2688,

    percentage: 42.19,

    elapsed_time: "0:06:31",

    remaining_time: "0:08:56",

    throughput: 1929.91,

    total_tokens: 755680
  },

  {
    current_steps: 105,

    total_steps: 237,

    loss: 1.5687,

    lr: 2.9775646485162694e-5,

    epoch: 1.3328,

    percentage: 44.3,

    elapsed_time: "0:06:47",

    remaining_time: "0:08:32",

    throughput: 1947.1,

    total_tokens: 793584
  },

  {
    current_steps: 110,

    total_steps: 237,

    loss: 1.617,

    lr: 2.8139906299080203e-5,

    epoch: 1.3968,

    percentage: 46.41,

    elapsed_time: "0:07:03",

    remaining_time: "0:08:09",

    throughput: 1961.87,

    total_tokens: 831616
  },

  {
    current_steps: 115,

    total_steps: 237,

    loss: 1.5906,

    lr: 2.6490378129245498e-5,

    epoch: 1.4607999999999999,

    percentage: 48.52,

    elapsed_time: "0:07:19",

    remaining_time: "0:07:45",

    throughput: 1979.96,

    total_tokens: 869584
  },

  {
    current_steps: 120,

    total_steps: 237,

    loss: 1.6022,

    lr: 2.483430539805344e-5,

    epoch: 1.5248,

    percentage: 50.63,

    elapsed_time: "0:07:31",

    remaining_time: "0:07:20",

    throughput: 2008.93,

    total_tokens: 907552
  },

  {
    current_steps: 125,

    total_steps: 237,

    loss: 1.5835,

    lr: 2.317896026643341e-5,

    epoch: 1.5888,

    percentage: 52.74,

    elapsed_time: "0:07:47",

    remaining_time: "0:06:59",

    throughput: 2019.79,

    total_tokens: 945232
  },

  {
    current_steps: 130,

    total_steps: 237,

    loss: 1.5439,

    lr: 2.1531611700273297e-5,

    epoch: 1.6528,

    percentage: 54.85,

    elapsed_time: "0:08:04",

    remaining_time: "0:06:38",

    throughput: 2031.19,

    total_tokens: 983552
  },

  {
    current_steps: 135,

    total_steps: 237,

    loss: 1.6142,

    lr: 1.989949355087339e-5,

    epoch: 1.7168,

    percentage: 56.96,

    elapsed_time: "0:08:21",

    remaining_time: "0:06:18",

    throughput: 2038.51,

    total_tokens: 1021872
  },

  {
    current_steps: 140,

    total_steps: 237,

    loss: 1.5687,

    lr: 1.8289772789595917e-5,

    epoch: 1.7808000000000002,

    percentage: 59.07,

    elapsed_time: "0:08:37",

    remaining_time: "0:05:58",

    throughput: 2050.25,

    total_tokens: 1060208
  },

  {
    current_steps: 145,

    total_steps: 237,

    loss: 1.5689,

    lr: 1.6709518036198308e-5,

    epoch: 1.8448,

    percentage: 61.18,

    elapsed_time: "0:08:54",

    remaining_time: "0:05:38",

    throughput: 2056.96,

    total_tokens: 1098640
  },

  {
    current_steps: 150,

    total_steps: 237,

    loss: 1.6486,

    lr: 1.5165668519048799e-5,

    epoch: 1.9088,

    percentage: 63.29,

    elapsed_time: "0:09:10",

    remaining_time: "0:05:19",

    throughput: 2064.77,

    total_tokens: 1135648
  },

  {
    current_steps: 155,

    total_steps: 237,

    loss: 1.626,

    lr: 1.3665003603526705e-5,

    epoch: 1.9727999999999999,

    percentage: 65.4,

    elapsed_time: "0:09:27",

    remaining_time: "0:05:00",

    throughput: 2069.02,

    total_tokens: 1173440
  },

  {
    current_steps: 160,

    total_steps: 237,

    loss: 1.6756,

    lr: 1.2214113022414448e-5,

    epoch: 2.0256,

    percentage: 67.51,

    elapsed_time: "0:09:37",

    remaining_time: "0:04:37",

    throughput: 2086.91,

    total_tokens: 1205472
  },

  {
    current_steps: 165,

    total_steps: 237,

    loss: 1.5016,

    lr: 1.08193679390058e-5,

    epoch: 2.0896,

    percentage: 69.62,

    elapsed_time: "0:09:50",

    remaining_time: "0:04:17",

    throughput: 2107.27,

    total_tokens: 1243920
  },

  {
    current_steps: 170,

    total_steps: 237,

    loss: 1.4533,

    lr: 9.486892969998465e-6,

    epoch: 2.1536,

    percentage: 71.73,

    elapsed_time: "0:10:07",

    remaining_time: "0:03:59",

    throughput: 2111.38,

    total_tokens: 1281712
  },

  {
    current_steps: 175,

    total_steps: 237,

    loss: 1.5425,

    lr: 8.222539291024078e-6,

    epoch: 2.2176,

    percentage: 73.84,

    elapsed_time: "0:10:19",

    remaining_time: "0:03:39",

    throughput: 2129.44,

    total_tokens: 1319600
  },

  {
    current_steps: 180,

    total_steps: 237,

    loss: 1.5943,

    lr: 7.031858942915187e-6,

    epoch: 2.2816,

    percentage: 75.95,

    elapsed_time: "0:10:36",

    remaining_time: "0:03:21",

    throughput: 2134.94,

    total_tokens: 1358768
  },

  {
    current_steps: 185,

    total_steps: 237,

    loss: 1.4804,

    lr: 5.920080451535295e-6,

    epoch: 2.3456,

    percentage: 78.06,

    elapsed_time: "0:10:50",

    remaining_time: "0:03:02",

    throughput: 2147.92,

    total_tokens: 1396912
  },

  {
    current_steps: 190,

    total_steps: 237,

    loss: 1.5183,

    lr: 4.892085868230881e-6,

    epoch: 2.4096,

    percentage: 80.17,

    elapsed_time: "0:11:03",

    remaining_time: "0:02:44",

    throughput: 2163.9,

    total_tokens: 1434688
  },

  {
    current_steps: 195,

    total_steps: 237,

    loss: 1.4963,

    lr: 3.952389331725162e-6,

    epoch: 2.4736000000000002,

    percentage: 82.28,

    elapsed_time: "0:11:15",

    remaining_time: "0:02:25",

    throughput: 2180.61,

    total_tokens: 1473360
  },

  {
    current_steps: 200,

    total_steps: 237,

    loss: 1.5036,

    lr: 3.1051172455930394e-6,

    epoch: 2.5376,

    percentage: 84.39,

    elapsed_time: "0:11:30",

    remaining_time: "0:02:07",

    throughput: 2188.78,

    total_tokens: 1511872
  },

  {
    current_steps: 205,

    total_steps: 237,

    loss: 1.5142,

    lr: 2.3539901583619185e-6,

    epoch: 2.6016,

    percentage: 86.5,

    elapsed_time: "0:11:45",

    remaining_time: "0:01:50",

    throughput: 2195.7,

    total_tokens: 1549664
  },

  {
    current_steps: 210,

    total_steps: 237,

    loss: 1.5715,

    lr: 1.7023064258068377e-6,

    epoch: 2.6656,

    percentage: 88.61,

    elapsed_time: "0:11:58",

    remaining_time: "0:01:32",

    throughput: 2208.75,

    total_tokens: 1586848
  },

  {
    current_steps: 215,

    total_steps: 237,

    loss: 1.4923,

    lr: 1.152927727181935e-6,

    epoch: 2.7296,

    percentage: 90.72,

    elapsed_time: "0:12:11",

    remaining_time: "0:01:14",

    throughput: 2222.45,

    total_tokens: 1624832
  },

  {
    current_steps: 220,

    total_steps: 237,

    loss: 1.5028,

    lr: 7.082664989897487e-7,

    epoch: 2.7936,

    percentage: 92.83,

    elapsed_time: "0:12:23",

    remaining_time: "0:00:57",

    throughput: 2235.67,

    total_tokens: 1662896
  },

  {
    current_steps: 225,

    total_steps: 237,

    loss: 1.5284,

    lr: 3.7027534146913676e-7,

    epoch: 2.8576,

    percentage: 94.94,

    elapsed_time: "0:12:38",

    remaining_time: "0:00:40",

    throughput: 2243.35,

    total_tokens: 1700624
  },

  {
    current_steps: 230,

    total_steps: 237,

    loss: 1.4604,

    lr: 1.4043844432016506e-7,

    epoch: 2.9215999999999998,

    percentage: 97.05,

    elapsed_time: "0:12:50",

    remaining_time: "0:00:23",

    throughput: 2256.43,

    total_tokens: 1739168
  },

  {
    current_steps: 235,

    total_steps: 237,

    loss: 1.4805,

    lr: 1.976506931745392e-8,

    epoch: 2.9856,

    percentage: 99.16,

    elapsed_time: "0:13:05",

    remaining_time: "0:00:06",

    throughput: 2263.68,

    total_tokens: 1777280
  },

  {
    current_steps: 237,

    total_steps: 237,

    epoch: 3.0,

    percentage: 100.0,

    elapsed_time: "0:13:10",

    remaining_time: "0:00:00",

    throughput: 2260.63,

    total_tokens: 1786704
  }
];
